import type { DocumentNode } from 'graphql';
// Api
import {
  UPDATE_CUSTOM_CAMPAIGN,
  UPDATE_DIRECT_BOOKING_CAMPAIGN,
} from 'api/campaignV2/mutations';
// Types
import { CampaignType } from 'api/graphql-global-types';
import {
  BuildUpdateInputArgs,
  buildUpdateCustomCampaignInput,
  buildUpdateDirectBookingCampaignInput,
} from 'helpers/customCampaign';

/** Selects which edit flow to run for custom-shaped campaigns. */
export enum CustomCampaignEditMode {
  Custom = 'Custom',
  DirectBooking = 'DirectBooking',
}

/** Step 1 UI: talent/audience filters vs single talent picker. */
export enum CustomCampaignStepOne {
  TalentAudience = 'TalentAudience',
  PreselectedTalent = 'PreselectedTalent',
}

export type CustomCampaignModeConfig = {
  title: string;
  successMessage: string;
  mutation: DocumentNode;
  mutationResultKey: 'updateCustomCampaign' | 'updateDirectBookingCampaign';
  buildUpdateInput: (
    args: BuildUpdateInputArgs
  ) =>
    | ReturnType<typeof buildUpdateCustomCampaignInput>
    | ReturnType<typeof buildUpdateDirectBookingCampaignInput>;
  stepOne: CustomCampaignStepOne;
  briefFields: {
    showApplicationWindow: boolean;
    showQuestions: boolean;
  };
  payment: {
    showReservedBudget: boolean;
    pricePerAthleteLabel: string;
  };
};

export const CUSTOM_CAMPAIGN_MODE_CONFIG: Record<
  CustomCampaignEditMode,
  CustomCampaignModeConfig
> = {
  [CustomCampaignEditMode.Custom]: {
    title: 'Edit Custom Campaign',
    successMessage: 'The custom campaign was successfully updated',
    mutation: UPDATE_CUSTOM_CAMPAIGN,
    mutationResultKey: 'updateCustomCampaign',
    buildUpdateInput: buildUpdateCustomCampaignInput,
    stepOne: CustomCampaignStepOne.TalentAudience,
    briefFields: {
      showApplicationWindow: true,
      showQuestions: true,
    },
    payment: {
      showReservedBudget: true,
      pricePerAthleteLabel: 'Price Per Talent',
    },
  },
  [CustomCampaignEditMode.DirectBooking]: {
    title: 'Edit Direct Booking Campaign',
    successMessage: 'The direct booking campaign was successfully updated',
    mutation: UPDATE_DIRECT_BOOKING_CAMPAIGN,
    mutationResultKey: 'updateDirectBookingCampaign',
    buildUpdateInput: buildUpdateDirectBookingCampaignInput,
    stepOne: CustomCampaignStepOne.PreselectedTalent,
    briefFields: {
      showApplicationWindow: false,
      showQuestions: false,
    },
    payment: {
      showReservedBudget: false,
      pricePerAthleteLabel: 'Offer to Talent',
    },
  },
};

export const resolveCustomCampaignEditMode = (
  campaignType: CampaignType
): CustomCampaignEditMode => {
  if (campaignType === CampaignType.DirectBooking) {
    return CustomCampaignEditMode.DirectBooking;
  }
  return CustomCampaignEditMode.Custom;
};
